import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
} from "@tanstack/react-router";
import { z } from "zod";

import { AUTH_CALLBACK_PATH } from "../lib/auth-config.ts";

import { ChatThread } from "../chat/ChatThread.tsx";
import { MechanicView } from "../mechanics/MechanicView.tsx";
import { BatchPanel } from "../work/BatchPanel.tsx";
import { KpisPanel } from "../work/KpisPanel.tsx";
import { OverviewPanel } from "../work/OverviewPanel.tsx";
import { ParticipantsPanel } from "../work/ParticipantsPanel.tsx";
import { PositionsPanel } from "../work/PositionsPanel.tsx";
import { RegionsPanel } from "../work/RegionsPanel.tsx";
import { UsagePanel } from "../work/UsagePanel.tsx";
import { AppShell, useWorkspace } from "./AppShell.tsx";
import { AuthCallback } from "./AuthCallback.tsx";
import { ChatShell } from "./ChatShell.tsx";
import { DashboardShell } from "./DashboardShell.tsx";
import { RequireSession } from "./RequireSession.tsx";
import type { SessionStore } from "./session.tsx";

/**
 * Las rutas de la aplicación.
 *
 * En código y no en archivos: son dos modos de trabajo (§9.2) con un puñado de
 * pantallas, y un generador que vigile una carpeta sería más maquinaria que
 * rutas.
 *
 * El árbol es el diseño: bajo la raíz --que solo pone el estado compartido--
 * cuelgan los DOS ARMAZONES, y cada pantalla nace dentro del suyo. Antes había
 * un armazón único para las tres pestañas, así que Work y Mecánicas heredaban
 * el historial de conversaciones y el selector de memoria, que allí no
 * significan nada.
 *
 * Lo que aporta el router aquí no es navegar --ya se navegaba-- sino que la
 * URL deje de manejarse a mano: un valor raro se valida en vez de caer en
 * silencio en la pantalla equivocada.
 *
 * Por encima de los armazones hay una capa más, la de la sesión: la raíz no
 * dibuja nada, `/auth/callback` cuelga de ella directamente --sin armazón,
 * porque el armazón arranca el chat y ahí aún no hay sesión--, y todo lo demás
 * cuelga de una ruta sin segmento que no deja pasar sin sesión.
 */
export interface RouterContext {
  readonly session: SessionStore;
}

/** `2026-06` es el mes; `2026`, el año entero. */
const PERIOD_PATTERN = /^\d{4}(-\d{2})?$/;

const searchSchema = z.object({
  /** La conversación abierta. Sobrevive a cambiar de modo, así que va aquí. */
  c: z.uuid().optional(),
  /**
   * El mes del panel. En la URL por la misma razón que la conversación:
   * recargar no lo pierde y el enlace que se comparte enseña lo mismo.
   *
   * El mes puede venir sin mes --`2026`-- y eso es un año entero. Lo entiende
   * el padrón, que filtra por año y mes por separado; las secciones que son
   * mensuales lo dicen en vez de preguntar por un periodo que no existe.
   */
  p: z
    .union([z.string(), z.number()])
    /* Un año entero viaja como `?p=2026`, y el router parsea lo que parece un
       número COMO un número. El periodo es texto --`2026-06` también lo es--,
       así que se devuelve a texto antes de mirarle la forma. Sin esto, elegir
       "Todo 2026" y recargar acaba en la pantalla de error del router. */
    .transform((value) => String(value))
    .refine(
      (value) => PERIOD_PATTERN.test(value),
      "El periodo se escribe AAAA-MM, o AAAA entero",
    )
    .optional(),
});

const rootRoute = createRootRouteWithContext<RouterContext>()({
  validateSearch: searchSchema,
  component: Outlet,
});

/** La vuelta del SSO (§10.3). Fuera del armazón. */
const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_CALLBACK_PATH,
  validateSearch: z.object({
    code: z.string().optional(),
    state: z.string().optional(),
    error: z.string().optional(),
  }),
  component: function CallbackRoute() {
    const params = callbackRoute.useSearch();
    const navigate = callbackRoute.useNavigate();
    return (
      <AuthCallback
        params={params}
        onDone={(returnTo) => {
          // `returnTo` es una ruta con su búsqueda (`/work?p=2026-06`), así
          // que va por el historial y no por `to`, que la tomaría por ruta.
          void navigate({ href: returnTo, replace: true });
        }}
      />
    );
  },
});

/**
 * Todo lo que necesita sesión. Sin segmento en la URL.
 *
 * `beforeLoad` recupera la sesión --o manda al SSO-- ANTES de que se monte
 * nada: el armazón arranca el chat, y el chat habla con el API. Lo que se
 * dibuja según cómo haya ido lo decide `RequireSession`, que es quien sabe
 * enseñar un fallo o un acceso denegado.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authenticated",
  beforeLoad: async ({ context, location }) => {
    await context.session.ensure(location.href);
  },
  component: () => (
    <RequireSession>
      <AppShell>
        <Outlet />
      </AppShell>
    </RequireSession>
  ),
});

/**
 * El armazón conversacional. No añade segmento a la URL: el chat es la raíz
 * --es donde se hacen las cosas-- y la mecánica cuelga de la memoria, que es
 * de donde se abre.
 */
const chatShellRoute = createRoute({
  getParentRoute: () => appRoute,
  id: "chat",
  component: ChatShell,
});

const chatRoute = createRoute({
  getParentRoute: () => chatShellRoute,
  path: "/",
  component: function ChatRoute() {
    const { chat, focusedMessage, clearFocusedMessage } = useWorkspace();
    return (
      <ChatThread
        chat={chat}
        density="full"
        focusMessageId={focusedMessage}
        onFocusHandled={clearFocusedMessage}
      />
    );
  },
});

/** El documento de una mecánica: lista en la memoria, documento aquí (§7.3). */
const mechanicRoute = createRoute({
  getParentRoute: () => chatShellRoute,
  path: "/memoria/mecanicas/$period/$slug",
  component: MechanicView,
});

/** El armazón del panel. Todo lo suyo cuelga de `/work`. */
const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/work",
  component: DashboardShell,
});

const overviewRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "/",
  component: OverviewPanel,
});

const participantsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "participantes",
  component: ParticipantsPanel,
});

/**
 * Los catálogos que la vinculación elige: cargos y regiones.
 *
 * Cuelgan del panel como una sección más --no de participantes-- porque se
 * administran por su cuenta: un cargo existe aunque hoy no lo tenga nadie.
 */
const positionsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "cargos",
  component: PositionsPanel,
});

const regionsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "regiones",
  component: RegionsPanel,
});

const kpisRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "kpis",
  component: KpisPanel,
});

/** Lo que se ha gastado en modelos y cuánto cuesta, estimado (2.4). */
const usageRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "consumo",
  component: UsagePanel,
});

/** El detalle de un lote no tiene entrada de menú: se llega preguntando. */
const batchRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "lotes/$batchId",
  component: BatchPanel,
});

/**
 * El árbol, aparte del router, para que una prueba pueda montarlo sobre un
 * historial en memoria sin arrastrar el router de la aplicación.
 */
export const routeTree = rootRoute.addChildren([
  callbackRoute,
  appRoute.addChildren([
    chatShellRoute.addChildren([chatRoute, mechanicRoute]),
    dashboardRoute.addChildren([
      overviewRoute,
      participantsRoute,
      positionsRoute,
      regionsRoute,
      kpisRoute,
      usageRoute,
      batchRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // La pone `App` al montar el `RouterProvider`: la sesión es de la
  // aplicación, no del módulo del router.
  context: { session: undefined as unknown as SessionStore },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
